// AVRASTRO i18n service — one translation layer for every module, print template and message.
// - stable dot-keys ("case.stage.underwriting"), messages in locales/<code>.json (flat or nested)
// - ICU plural/select/ordinal via Intl.PluralRules; numbers, currency, dates, relative time via Intl
// - runtime locale switching, fallback chain (e.g. pt-BR → pt → en), RTL metadata, missing-key diagnostics
// - locale-aware collation for sorting/searching people
import { parse, render, IcuSyntaxError, type Node, type Params, type FormatEnv } from './icu.ts';
import en from './locales/en.json' with { type: 'json' };
import el from './locales/el.json' with { type: 'json' };
import fr from './locales/fr.json' with { type: 'json' };
import de from './locales/de.json' with { type: 'json' };
import es from './locales/es.json' with { type: 'json' };
import it from './locales/it.json' with { type: 'json' };
import pt from './locales/pt.json' with { type: 'json' };
import ro from './locales/ro.json' with { type: 'json' };
import bg from './locales/bg.json' with { type: 'json' };
import pl from './locales/pl.json' with { type: 'json' };
import ru from './locales/ru.json' with { type: 'json' };
import uk from './locales/uk.json' with { type: 'json' };
import ar from './locales/ar.json' with { type: 'json' };
import he from './locales/he.json' with { type: 'json' };
import tr from './locales/tr.json' with { type: 'json' };
import nl from './locales/nl.json' with { type: 'json' };

export type { Params } from './icu.ts';
export { parse as parseMessage, IcuSyntaxError } from './icu.ts';

export interface LocaleInfo {
  code: string;
  /** endonym shown in the language picker */
  name: string;
  englishName: string;
  dir: 'ltr' | 'rtl';
  /** complete = every key reviewed; partial = UI shell translated, rest falls back */
  status: 'complete' | 'partial';
}

export const LOCALES: LocaleInfo[] = [
  { code: 'el', name: 'Ελληνικά', englishName: 'Greek', dir: 'ltr', status: 'complete' },
  { code: 'en', name: 'English', englishName: 'English', dir: 'ltr', status: 'complete' },
  { code: 'fr', name: 'Français', englishName: 'French', dir: 'ltr', status: 'partial' },
  { code: 'de', name: 'Deutsch', englishName: 'German', dir: 'ltr', status: 'partial' },
  { code: 'es', name: 'Español', englishName: 'Spanish', dir: 'ltr', status: 'partial' },
  { code: 'it', name: 'Italiano', englishName: 'Italian', dir: 'ltr', status: 'partial' },
  { code: 'pt', name: 'Português', englishName: 'Portuguese', dir: 'ltr', status: 'partial' },
  { code: 'ro', name: 'Română', englishName: 'Romanian', dir: 'ltr', status: 'partial' },
  { code: 'bg', name: 'Български', englishName: 'Bulgarian', dir: 'ltr', status: 'partial' },
  { code: 'pl', name: 'Polski', englishName: 'Polish', dir: 'ltr', status: 'partial' },
  { code: 'ru', name: 'Русский', englishName: 'Russian', dir: 'ltr', status: 'complete' },
  { code: 'uk', name: 'Українська', englishName: 'Ukrainian', dir: 'ltr', status: 'partial' },
  { code: 'nl', name: 'Nederlands', englishName: 'Dutch', dir: 'ltr', status: 'partial' },
  { code: 'tr', name: 'Türkçe', englishName: 'Turkish', dir: 'ltr', status: 'partial' },
  { code: 'ar', name: 'العربية', englishName: 'Arabic', dir: 'rtl', status: 'partial' },
  { code: 'he', name: 'עברית', englishName: 'Hebrew', dir: 'rtl', status: 'partial' },
];

type Tree = { [k: string]: string | Tree };
const RAW: Record<string, Tree> = { en, el, fr, de, es, it, pt, ro, bg, pl, ru, uk, ar, he, tr, nl } as unknown as Record<string, Tree>;

/** Flatten nested JSON into dot keys. Keys starting with "_" (comments/meta) are ignored. */
export function flatten(tree: Tree, prefix = '', out: Record<string, string> = {}): Record<string, string> {
  for (const [k, v] of Object.entries(tree)) {
    if (k.startsWith('_')) continue;
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'string') out[key] = v; else flatten(v, key, out);
  }
  return out;
}

const CATALOGS = new Map<string, Record<string, string>>();
export function catalog(code: string): Record<string, string> {
  let c = CATALOGS.get(code);
  if (!c) { c = RAW[code] ? flatten(RAW[code]!) : {}; CATALOGS.set(code, c); }
  return c;
}
/** Register or extend a locale at runtime (plug-in language packs, market packs, customer overrides). */
export function registerMessages(code: string, tree: Tree) {
  RAW[code] = { ...(RAW[code] ?? {}), ...tree };
  CATALOGS.delete(code);
}

export function localeInfo(code: string): LocaleInfo {
  const base = code.split('-')[0]!;
  return LOCALES.find((l) => l.code === code) ?? LOCALES.find((l) => l.code === base) ?? { code, name: code, englishName: code, dir: 'ltr', status: 'partial' };
}

export function fallbackChain(code: string, fallback = 'en'): string[] {
  const chain = [code];
  const base = code.split('-')[0]!;
  if (base !== code) chain.push(base);
  if (!chain.includes(fallback)) chain.push(fallback);
  return chain;
}

export interface MissingKeyReport { key: string; locale: string }

export interface I18n {
  locale: string;
  dir: 'ltr' | 'rtl';
  currency: string;
  timeZone?: string;
  t(key: string, params?: Params): string;
  has(key: string): boolean;
  /** translate if the string is a known key, otherwise return it verbatim (user content is never translated) */
  maybe(textOrKey: string | null | undefined, params?: Params): string;
  enumLabel(enumName: string, value: string | null | undefined): string;
  number(n: number, opts?: Intl.NumberFormatOptions): string;
  money(n: number | null | undefined, currency?: string, opts?: { compact?: boolean }): string;
  percent(n: number): string;
  date(d: string | Date | null | undefined, style?: 'short' | 'medium' | 'long' | 'weekday' | 'dayMonth'): string;
  time(t: string | Date | null | undefined): string;
  dateTime(iso: string | null | undefined): string;
  relativeDays(days: number): string;
  list(items: string[], type?: 'conjunction' | 'disjunction'): string;
  compare(a: string, b: string): number;
  missing(): MissingKeyReport[];
}

const MISSING: MissingKeyReport[] = [];
const PARSED = new Map<string, Node[] | IcuSyntaxError>();

function parsedFor(locale: string, key: string, msg: string): Node[] | IcuSyntaxError {
  const id = `${locale}\u0000${key}`;
  let p = PARSED.get(id);
  if (!p) {
    try { p = parse(msg); } catch (e) { p = e instanceof IcuSyntaxError ? e : new IcuSyntaxError(String(e)); }
    PARSED.set(id, p);
  }
  return p;
}

function parseDay(d: string | Date): Date {
  if (d instanceof Date) return d;
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) { const [y, m, x] = d.split('-').map(Number); return new Date(y!, m! - 1, x!, 12); }
  return new Date(d);
}

export function createI18n(opts: { locale: string; fallback?: string; currency?: string; timeZone?: string; onMissing?: (r: MissingKeyReport) => void }): I18n {
  const locale = opts.locale;
  const chain = fallbackChain(locale, opts.fallback ?? 'en');
  const currency = opts.currency ?? 'EUR';
  const info = localeInfo(locale);
  const intlLocale = (() => { try { return Intl.getCanonicalLocales(locale)[0] ?? 'en'; } catch { return 'en'; } })();
  const nf = new Intl.NumberFormat(intlLocale);
  const pr = new Intl.PluralRules(intlLocale);
  const po = new Intl.PluralRules(intlLocale, { type: 'ordinal' });
  const collator = new Intl.Collator(intlLocale, { sensitivity: 'base', numeric: true });
  const tz = opts.timeZone ? { timeZone: opts.timeZone } : {};
  const dateFormats: Record<string, Intl.DateTimeFormatOptions> = {
    short: { day: '2-digit', month: '2-digit', year: 'numeric' },
    medium: { day: 'numeric', month: 'short', year: 'numeric' },
    long: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
    weekday: { weekday: 'short', day: 'numeric', month: 'short' },
    dayMonth: { day: 'numeric', month: 'short' },
  };
  const dfCache = new Map<string, Intl.DateTimeFormat>();
  const df = (style: string) => {
    let f = dfCache.get(style);
    if (!f) { f = new Intl.DateTimeFormat(intlLocale, { ...(dateFormats[style] ?? dateFormats.medium), ...tz }); dfCache.set(style, f); }
    return f;
  };
  const tf = new Intl.DateTimeFormat(intlLocale, { hour: '2-digit', minute: '2-digit', ...tz });
  const rtf = new Intl.RelativeTimeFormat(intlLocale, { numeric: 'auto' });

  const env: FormatEnv = {
    locale: intlLocale, currency,
    number: (n, style) => style === 'currency' ? api.money(n) : style === 'percent' ? api.percent(n) : style === 'integer' ? new Intl.NumberFormat(intlLocale, { maximumFractionDigits: 0 }).format(n) : nf.format(n),
    date: (d, style) => api.date(d, (style as 'short') ?? 'medium'),
    time: (d) => api.time(d),
    plural: (n, ordinal) => (ordinal ? po : pr).select(n),
  };

  const lookup = (key: string): [string, string] | null => {
    for (const code of chain) { const m = catalog(code)[key]; if (m !== undefined) return [code, m]; }
    return null;
  };

  const api: I18n = {
    locale, dir: info.dir, currency, timeZone: opts.timeZone,
    t(key, params = {}) {
      const hit = lookup(key);
      if (!hit) {
        const r = { key, locale };
        if (!MISSING.some((m) => m.key === key && m.locale === locale)) { MISSING.push(r); opts.onMissing?.(r); }
        return key;
      }
      if (hit[0] !== locale && hit[0] !== locale.split('-')[0] && !MISSING.some((m) => m.key === key && m.locale === locale)) MISSING.push({ key, locale });
      const nodes = parsedFor(hit[0], key, hit[1]);
      if (nodes instanceof IcuSyntaxError) return hit[1];
      return render(nodes, params, env);
    },
    has: (key) => lookup(key) !== null,
    maybe(s, params) {
      if (!s) return '';
      if (/^[a-z][a-zA-Z0-9_]*(\.[a-zA-Z0-9_]+)+$/.test(s) && lookup(s)) return api.t(s, params);
      return s;
    },
    enumLabel(name, value) {
      if (!value) return '';
      const k = `enum.${name}.${value}`;
      return lookup(k) ? api.t(k) : value;
    },
    number: (n, o) => (o ? new Intl.NumberFormat(intlLocale, o).format(n) : nf.format(n)),
    money(n, cur, o) {
      if (n === null || n === undefined || !Number.isFinite(n)) return '—';
      return new Intl.NumberFormat(intlLocale, { style: 'currency', currency: cur ?? currency, maximumFractionDigits: o?.compact ? 0 : 2, notation: o?.compact && Math.abs(n) >= 100000 ? 'compact' : 'standard' }).format(n);
    },
    percent: (n) => new Intl.NumberFormat(intlLocale, { style: 'percent', maximumFractionDigits: 1 }).format(n),
    date(d, style = 'medium') {
      if (!d) return '—';
      const x = parseDay(d);
      return isNaN(x.getTime()) ? String(d) : df(style).format(x);
    },
    time(t) {
      if (!t) return '';
      if (typeof t === 'string' && /^\d{2}:\d{2}$/.test(t)) { const [h, m] = t.split(':').map(Number); return tf.format(new Date(2000, 0, 1, h!, m!)); }
      const x = t instanceof Date ? t : new Date(t);
      return isNaN(x.getTime()) ? String(t) : tf.format(x);
    },
    dateTime(iso) { if (!iso) return '—'; const d = new Date(iso); return isNaN(d.getTime()) ? iso : `${df('medium').format(d)} ${tf.format(d)}`; },
    relativeDays: (days) => rtf.format(days, 'day'),
    list: (items, type = 'conjunction') => new Intl.ListFormat(intlLocale, { style: 'long', type }).format(items),
    compare: (a, b) => collator.compare(a, b),
    missing: () => MISSING.filter((m) => m.locale === locale),
  };
  return api;
}

// ---------------------------------------------------------------------------
// Diagnostics used by tests and the Diagnostics screen

export function catalogReport() {
  const base = catalog('en');
  const baseKeys = Object.keys(base);
  return LOCALES.map((l) => {
    const c = catalog(l.code);
    const keys = Object.keys(c);
    const missing = baseKeys.filter((k) => !(k in c));
    const extra = keys.filter((k) => !(k in base));
    const syntax: string[] = [];
    for (const [k, m] of Object.entries(c)) { try { parse(m); } catch { syntax.push(k); } }
    return { code: l.code, status: l.status, total: baseKeys.length, translated: keys.length - extra.length, coverage: (keys.length - extra.length) / baseKeys.length, missing, extra, syntax };
  });
}
